import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { readJson, storageKey, writeJson } from '../data/local/storage';

export type ThemePreference = 'SYSTEM' | 'LIGHT' | 'DARK';

const KEY = storageKey('theme');

interface ThemeValue {
  readonly preference: ThemePreference;
  setPreference(preference: ThemePreference): void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

/**
 * Preferencia de tema.
 *
 * Por defecto sigue al sistema. Al elegir explícitamente se marca `data-theme` en el elemento
 * raíz, lo que permite que la elección del usuario gane sobre `prefers-color-scheme` en ambas
 * direcciones (forzar claro con el sistema en oscuro, y al revés).
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => readJson<ThemePreference>(KEY) ?? 'SYSTEM',
  );

  useEffect(() => {
    const root = document.documentElement;
    if (preference === 'SYSTEM') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', preference === 'DARK' ? 'dark' : 'light');
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    writeJson(KEY, next);
  }, []);

  const value = useMemo(() => ({ preference, setPreference }), [preference, setPreference]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme debe usarse dentro de <ThemeProvider>');
  return value;
}
